import React, { useEffect, useRef } from 'react'
import { Typography } from '@grammarly/badger-ui'
import { PlagiarismChartDescription } from './chart-description'
import { useBrowserService } from '../../../../../lib/hooks'
import { FeatureErrorsType } from './types'
import { PlagiarismHelper } from './helpers/plagiarism-helper'
import { Counter } from './counter'
import { getTotalErrors } from './helpers'
import { IconNoErrors, ReportSummaryCircle } from './icons'
import { SignUpButton } from '../form/ctas'

import style from './report-summary.module.scss'

interface FeatureErrorProps {
  error: FeatureErrorsType
  color: 'goldOnYellow' | 'whiteOnRed' | 'whiteOnGold'
}

const Feature: React.FC<FeatureErrorProps> = (props) => {
  const { feature, total } = props.error
  const isPlagiarism = feature.alertName === 'Originality'

  return (
    <div className={style.errorMessage}>
      <Typography.TS as="span">{feature.title}</Typography.TS>
      {total > 0 ? (
        <span className={style.alertCount}>
          <Counter
            multiplier={2.5}
            alertCount={isPlagiarism ? '!' : total}
            ariaHidden={isPlagiarism}
            color={props.color}
          />
        </span>
      ) : (
        <IconNoErrors className={style.errorMessageIcon} />
      )}
    </div>
  )
}

interface ReportSummaryProps {
  errors: FeatureErrorsType[]
  isForPlagiarismPlanPage: boolean
}

export const ReportSummary: React.FC<ReportSummaryProps> = ({ errors, isForPlagiarismPlanPage }) => {
  const { isMobileOrTablet } = useBrowserService()
  const plagiarismHelper: PlagiarismHelper = new PlagiarismHelper(errors)
  const totalErrors = getTotalErrors(errors)
  const ref = useRef<HTMLDivElement>(null)
  const featureColor = 'whiteOnRed'

  useEffect(() => {
    if (isMobileOrTablet && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [isMobileOrTablet])

  return (
    <div className={style.reportContainer} ref={ref} data-component-name="report-summary">
      <div className={style.topSection}>
        <div className={style.reportSummaryText}>
          <ReportSummaryCircle totalErrors={totalErrors} plagiarism={plagiarismHelper} color="whiteOnRed" />
          <PlagiarismChartDescription totalErrors={totalErrors} plagiarism={plagiarismHelper} />
        </div>
        <ul className={style.reportList}>
          {errors.map((error) => (
            <li key={error.group} className={style.reportListItem}>
              <Feature error={error} color={featureColor} />
            </li>
          ))}
        </ul>
      </div>
      <SignUpButton placement="report" isForPlagiarismPlanPage={isForPlagiarismPlanPage} />
    </div>
  )
}
